import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { requestUpload, completeUpload, abortUpload, refreshUpload, createItem, uploadBytes } from '../services/api';
import { qk } from '../services/queryKeys';
import { extractVideoPoster } from '../utils/videoPoster';

export type UploadStage = 'idle' | 'uploading' | 'completing' | 'scanning' | 'done' | 'error';

export interface UploadFile {
  id: string;
  file: File;
  stage: UploadStage;
  progress: number;
  error?: string;
  posterUrl?: string; // client-generated video poster, shown during upload
}

// Files above this size are split into parts and uploaded via S3 multipart —
// below it, a single presigned PUT is simpler and has less overhead.
const MULTIPART_THRESHOLD_BYTES = 20 * 1024 * 1024; // 20 MiB
// S3 requires every part but the last to be >= 5 MiB; stay comfortably above that.
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024; // 8 MiB
const MAX_CONCURRENT_PARTS = 3;
const MAX_PART_RETRIES = 3;
// Re-presign part URLs this long before they expire, so a slow upload never
// PUTs against an already-dead signature (the server TTL is 15m by default).
const REFRESH_MARGIN_MS = 90 * 1000;

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function fileIcon(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'music';
  if (/zip|compressed|tar|rar|7z/.test(mime)) return 'archive';
  return 'file';
}

// Uploads every part with bounded concurrency, returning {partNumber, etag} in
// part order regardless of completion order. Presigned part URLs are refreshed
// both proactively (as the session TTL approaches) and reactively (a failed
// part is most often an expired signature on a slow connection) — so uploads
// that run longer than the URL lifetime still complete instead of failing.
async function uploadPartsConcurrently(
  sessionId: string,
  initialExpiresAt: string,
  partMetas: { partNumber: number; url: string }[],
  file: File,
  onPartProgress: (partNumber: number, loaded: number) => void,
  signal: AbortSignal,
): Promise<{ partNumber: number; etag: string }[]> {
  const parts: { partNumber: number; etag: string }[] = new Array(partMetas.length);
  const urlByPart = new Map(partMetas.map((p) => [p.partNumber, p.url]));
  const completed = new Set<number>();
  let expiresAtMs = Date.parse(initialExpiresAt);
  let refreshInFlight: Promise<void> | null = null;
  let nextIndex = 0;

  // Coalesced refresh: concurrent workers that all notice expiry share one
  // request. Re-presigns only the parts still pending, extends the deadline.
  function refreshUrls(): Promise<void> {
    if (refreshInFlight) return refreshInFlight;
    const pending = partMetas.map((p) => p.partNumber).filter((n) => !completed.has(n));
    refreshInFlight = refreshUpload(sessionId, pending)
      .then((res) => {
        for (const p of res.part_urls ?? []) urlByPart.set(p.partNumber, p.url);
        if (res.expires_at) expiresAtMs = Date.parse(res.expires_at);
      })
      .finally(() => { refreshInFlight = null; });
    return refreshInFlight;
  }

  async function putPart(partNumber: number, blob: Blob, onProgress: (loaded: number) => void): Promise<string> {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt++) {
      if (signal.aborted) throw new DOMException('Upload aborted', 'AbortError');
      // Proactive: refresh before the signature is due to expire.
      if (Date.now() > expiresAtMs - REFRESH_MARGIN_MS) await refreshUrls().catch(() => undefined);
      try {
        const etag = await uploadBytes(urlByPart.get(partNumber) as string, blob, onProgress, signal);
        if (!etag) throw new Error('Storage did not return an ETag for this part');
        return etag;
      } catch (err) {
        if (signal.aborted) throw err; // don't retry a user-cancelled upload
        lastErr = err;
        onProgress(0); // reset this part's contribution before retrying
        // Reactive: a failed PUT is most often an expired signature — get fresh
        // URLs before the next attempt (no-op'd/coalesced if one's in flight).
        await refreshUrls().catch(() => undefined);
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('Part upload failed after retries');
  }

  async function worker() {
    while (nextIndex < partMetas.length) {
      const idx = nextIndex++;
      const { partNumber } = partMetas[idx];
      const start = (partNumber - 1) * CHUNK_SIZE_BYTES;
      const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
      const blob = file.slice(start, end);
      const etag = await putPart(partNumber, blob, (loaded) => onPartProgress(partNumber, loaded));
      parts[idx] = { partNumber, etag };
      completed.add(partNumber);
      onPartProgress(partNumber, end - start);
    }
  }

  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_PARTS, partMetas.length) }, () => worker()));
  return parts;
}

export type UploadView = 'closed' | 'open' | 'minimized';

interface UploadTarget { spaceId?: string; folderId?: string; }

interface UploadContextValue {
  files: UploadFile[];
  view: UploadView;
  activeCount: number;
  doneCount: number;
  errorCount: number;
  aggregateProgress: number;
  openUpload: (target: UploadTarget) => void;
  minimize: () => void;
  expand: () => void;
  requestClose: () => void; // backdrop/X: minimize if active, else close
  cancelAll: () => void;    // abort everything and dismiss
  addFiles: (incoming: File[]) => void;
  cancelFile: (id: string) => void; // abort in-flight upload + drop the row
  removeFile: (id: string) => void; // drop a finished/errored row
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function useUpload(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUpload must be used within an UploadProvider');
  return ctx;
}

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [view, setView] = useState<UploadView>('closed');
  const qc = useQueryClient();
  const targetRef = useRef<UploadTarget>({});
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const update = useCallback((id: string, patch: Partial<UploadFile>) =>
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f)), []);

  const uploadFile = useCallback(async (uf: UploadFile, controller: AbortController) => {
    const { spaceId, folderId } = targetRef.current;
    if (!spaceId) {
      update(uf.id, { stage: 'error', error: 'No space selected' });
      return;
    }
    const signal = controller.signal;
    let sessionId: string | undefined;
    try {
      update(uf.id, { stage: 'uploading', progress: 0 });
      const mimeType = uf.file.type || 'application/octet-stream';
      const size = uf.file.size;
      const useMultipart = size > MULTIPART_THRESHOLD_BYTES;
      const partCount = useMultipart ? Math.ceil(size / CHUNK_SIZE_BYTES) : undefined;

      const res = await requestUpload(spaceId, uf.file.name, size, mimeType, useMultipart ? 'multipart' : 'single', partCount);
      const session = res.upload ?? res;
      sessionId = session.session_id;

      let parts: { partNumber: number; etag: string }[] | undefined;

      if (useMultipart) {
        const loadedByPart = new Map<number, number>();
        const reportProgress = (partNumber: number, loaded: number) => {
          loadedByPart.set(partNumber, loaded);
          const total = Array.from(loadedByPart.values()).reduce((a, b) => a + b, 0);
          update(uf.id, { progress: Math.round((total / size) * 100) });
        };
        parts = await uploadPartsConcurrently(session.session_id, session.expires_at, session.part_urls, uf.file, reportProgress, signal);
      } else {
        await uploadBytes(session.upload_url, uf.file, (loaded) => {
          update(uf.id, { progress: Math.round((loaded / size) * 100) });
        }, signal);
      }

      update(uf.id, { stage: 'completing', progress: 100 });
      const completed = await completeUpload(sessionId!, parts);
      const fileId = completed.file_id;

      update(uf.id, { stage: 'scanning' });
      await createItem(spaceId, fileId, uf.file.name, folderId);

      update(uf.id, { stage: 'done' });
      qc.invalidateQueries({ queryKey: qk.items(spaceId, folderId) });
      qc.invalidateQueries({ queryKey: qk.folders(spaceId, folderId) });
      qc.invalidateQueries({ queryKey: qk.usage() });
    } catch (err: unknown) {
      if (sessionId) abortUpload(sessionId);
      // A user-initiated cancel already removed the row — don't flag it as an error.
      if (signal.aborted) return;
      const msg = err instanceof Error ? err.message : 'Upload failed';
      update(uf.id, { stage: 'error', error: msg });
    } finally {
      controllersRef.current.delete(uf.id);
    }
  }, [qc, update]);

  const addFiles = useCallback((incoming: File[]) => {
    const newFiles: UploadFile[] = incoming.map(f => ({
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
      file: f,
      stage: 'idle' as UploadStage,
      progress: 0,
    }));
    setFiles(prev => [...prev, ...newFiles]);
    newFiles.forEach(uf => {
      const controller = new AbortController();
      controllersRef.current.set(uf.id, controller);
      uploadFile(uf, controller);
      // Grab a local poster frame for videos so the row shows a real preview
      // while the (backend-generated) card thumbnail is still being produced.
      if (uf.file.type.startsWith('video/')) {
        extractVideoPoster(uf.file).then(poster => { if (poster) update(uf.id, { posterUrl: poster }); });
      }
    });
  }, [uploadFile]);

  const openUpload = useCallback((target: UploadTarget) => {
    targetRef.current = target;
    // Starting fresh when nothing is in flight keeps stale finished rows from
    // a previous batch out of the new session.
    setFiles(prev => prev.some(f => f.stage !== 'done' && f.stage !== 'error') ? prev : []);
    setView('open');
  }, []);

  const minimize = useCallback(() => setView('minimized'), []);
  const expand = useCallback(() => setView('open'), []);

  const cancelFile = useCallback((id: string) => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const cancelAll = useCallback(() => {
    controllersRef.current.forEach(c => c.abort());
    controllersRef.current.clear();
    setFiles([]);
    setView('closed');
  }, []);

  const activeCount = files.filter(f => f.stage === 'idle' || f.stage === 'uploading' || f.stage === 'completing' || f.stage === 'scanning').length;
  const doneCount = files.filter(f => f.stage === 'done').length;
  const errorCount = files.filter(f => f.stage === 'error').length;

  const requestClose = useCallback(() => {
    // If anything is still uploading, stash to the dock instead of tearing the
    // session down; otherwise fully close and clear.
    if (activeCount > 0) { setView('minimized'); return; }
    setFiles([]);
    setView('closed');
  }, [activeCount]);

  // Auto-dismiss the dock a few seconds after everything finishes cleanly.
  useEffect(() => {
    if (view !== 'minimized' || files.length === 0) return;
    if (activeCount > 0 || errorCount > 0) return;
    const t = setTimeout(() => { setFiles([]); setView('closed'); }, 4000);
    return () => clearTimeout(t);
  }, [view, files.length, activeCount, errorCount]);

  const totalBytes = files.reduce((sum, f) => sum + f.file.size, 0);
  const loadedBytes = files.reduce((sum, f) => sum + (f.stage === 'done' ? f.file.size : (f.file.size * f.progress) / 100), 0);
  const aggregateProgress = totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;

  const value: UploadContextValue = {
    files, view, activeCount, doneCount, errorCount, aggregateProgress,
    openUpload, minimize, expand, requestClose, cancelAll, addFiles, cancelFile, removeFile,
  };

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}
